import { useCallback, useEffect, useMemo, useState } from "react";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import useApiStatus from "../../../../../hooks/useApiStatus";
import OrderService from "../../../../../service/OrderService";
import { hasPhoneNumber } from "../../../../../utilities/hasPhoneNumber";

const useChat = ({ idorder }) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const {
    isLoading: isLoadingGetMessages,
    setIsLoading: setIsLoadingGetMessages,
    setHasError,
    hasError,
  } = useApiStatus({
    isLoading: true,
  });
  const { isLoading: isLoadingSendMessage, setIsLoading: setIsLoadingSendMessage } = useApiStatus();
  const { isLoading: isLoadingRetryGet, setIsLoading: setIsLoadingRetryGet } = useApiStatus();
  const { showErrorToast } = useToastContext();

  const orderService = useMemo(() => new OrderService(), []);

  const sendMessage = async ({ userTo }) => {
    try {
      if (!message.length === 0) return;
      if (message.length > 250) {
        showErrorToast({ detail: "cannotExceed250Chars" });
        return;
      }
      if (hasPhoneNumber(message)) {
        showErrorToast({ detail: "messageHasPersonalNumber" });
        return;
      }

      setIsLoadingSendMessage(true);

      const res = await orderService.chatSendMessage({
        idorder,
        userFrom: localStorage.getItem("__extraconomy_username"),
        userTo: userTo,
        message: message,
      });

      if (res.messageCode !== 200000) {
        showErrorToast({ code: res.messageCode });
        setIsLoadingSendMessage(false);
      } else {
        setMessage("");
        await getChatMessages(setIsLoadingRetryGet);
        setIsLoadingSendMessage(false);
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setIsLoadingSendMessage(false);
      console.log(e);
    }
  };

  const getChatMessages = useCallback(
    async (setIsLoading) => {
      try {
        setHasError(false);
        setIsLoading(true);

        const res = await orderService.getChatMessages({
          idorder,
        });

        if (res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          setHasError(true);
          setIsLoading(false);
        } else {
          setMessages(res.result);

          if (res.result.length > 0) {
            const username = localStorage.getItem("__extraconomy_username");
            const messagesNotViewed = res.result.filter(
              (message) => !message.visualized && message.userTo.username === username
            );

            messagesNotViewed.forEach(async (message) => {
              const resMessagesViewed = await orderService.setMessagesViewed({
                idchatmessage: message?.id,
                userTo: message.userTo.username,
              });

              if (resMessagesViewed.messageCode !== 200000) {
                showErrorToast({ code: resMessagesViewed.messageCode });
              }
            });
          }

          setIsLoading(false);
        }
      } catch (e) {
        if (e.status === 401 || e.status === 403) {
          localStorage.removeItem("__extraconomy_token");
          localStorage.removeItem("__extraconomy_username");
          window.location.replace("/login");
        }
        setIsLoading(false);
        setHasError(true);
        console.log(e);
      }
    },
    [idorder, orderService, showErrorToast, setHasError]
  );

  useEffect(() => {
    if (idorder) getChatMessages(setIsLoadingGetMessages);
  }, [getChatMessages, idorder, setIsLoadingGetMessages]);

  useEffect(() => {
    const fetchInterval = setInterval(() => {
      if (idorder) getChatMessages(setIsLoadingRetryGet);
    }, 30000); // 30 seconds

    return () => clearInterval(fetchInterval);
  }, [getChatMessages, idorder, setIsLoadingRetryGet]);

  return {
    sendMessage,
    setMessage,
    messages,
    message,
    hasError,
    setMessages,
    isLoadingGetMessages,
    isLoadingRetryGet,
    setIsLoadingSendMessage,
    isLoadingSendMessage,
    setIsLoadingGetMessages,
  };
};

export default useChat;
